/**
 * GurbetciCard, Storefront-Tracking (assets/gc-tracking.js).
 * Stand 04.09.2026, Version 1. PLAN-TRACKING-2026-09-04 §2.2, Baustein D.
 *
 * Läuft als `defer`-Skript, eingehängt von snippets/gc-gtm.liquid und damit nur, wenn der
 * Funktionsschalter `tracking_live` steht. Es ergänzt die dataLayer-Schicht aus
 * snippets/gc-datalayer.liquid um alles, was erst im Browser passiert:
 *
 *   (a) Cart-Hook: umhüllt window.fetch und XMLHttpRequest für /cart/add, /cart/change,
 *       /cart/update, /cart/clear (mit und ohne .js) sowie Formulare auf /cart/add.
 *       Nach einer erfolgreichen Änderung wird der Warenkorb gelesen und gegen den
 *       letzten Stand verglichen; die Differenz je Zeile ergibt add_to_cart und
 *       remove_from_cart mit Items aus der Cart-Antwort.
 *   (b) select_item bei Klick auf Tarif- und Produktkarten und bei Wahl einer Paketkachel.
 *   (c) gc_language_switch bei Sprach- oder Länderwechsel im Lokalisierungsformular.
 *   (d) gc_destination_select und gc_travel_dates aus der Kaufbox (Reiseziel, Reisezeitraum).
 *   (e) cid-Erfassung: mit Analytics-Einwilligung und vorhandenem _ga-Cookie einmal je
 *       Sitzung POST /cart/update.js mit den Attributen _ga_cid, _gclid (nur mit
 *       Marketing-Einwilligung), _gc_consent (a1m1 | a1m0) und _gc_herkunft, damit
 *       gc-core den Server-Nachlauf der Bestellung derselben Sitzung zuordnen kann.
 *       Wird die Einwilligung in derselben Sitzung widerrufen, räumt das Skript die
 *       Attribute wieder ab (_gc_consent a0m0).
 *   (f) Herkunft: Kampagnenparameter (utm_source bis utm_id) und Klick-Kennungen
 *       (gclid, gbraid, wbraid, dclid) werden beim Eintritt festgehalten und gepuffert
 *       weitergereicht, damit ein Kauf Tage nach dem Klick nicht als "direkt" zählt.
 *       Zwei Fenster: 30 Minuten für Kampagnenparameter, 90 Tage für Klick-Kennungen.
 *
 * Grundsätze: nichts Personenbezogenes (R-S3), keine Kunden-ID, keine Empfängerdaten;
 * jeder Schritt in try/catch, kein Aufruf blockiert den Kauf. Eigene Ereignisse tragen
 * das Präfix gc_, Standardnamen bleiben GA4 (Learning L11). item_id im Format
 * shopify_ZZ_<product_id>_<variant_id>, nie hartkodiert.
 *
 * Kein Kundentext in dieser Datei.
 */
(function (w, d) {
  'use strict';
  if (w.gcTrackingGeladen) return;
  w.gcTrackingGeladen = true;

  const gc = (w.gcTracking = w.gcTracking || {});
  const kontext = gc.context || {};
  const katalog = Array.isArray(gc.katalog) ? gc.katalog : [];
  const urFetch = typeof w.fetch === 'function' ? w.fetch : null;

  /* ------------------------------------------------------------------ Hilfen */

  function push(objekt) {
    try {
      (w.dataLayer = w.dataLayer || []).push(objekt);
    } catch (fehler) {
      /* nie blockieren */
    }
  }

  function ecommerce(name, daten) {
    push({ ecommerce: null });
    push({ event: name, ecommerce: daten });
  }

  function cookie(name) {
    try {
      const treffer = d.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
      return treffer ? decodeURIComponent(treffer.pop()) : '';
    } catch (fehler) {
      return '';
    }
  }

  function speicher(art, schluessel, wert) {
    try {
      const s = art === 'session' ? w.sessionStorage : w.localStorage;
      if (wert === undefined) return s.getItem(schluessel);
      if (wert === null) s.removeItem(schluessel);
      else s.setItem(schluessel, wert);
    } catch (fehler) {
      return null;
    }
    return null;
  }

  function nummer(wert) {
    return String(wert == null ? '' : wert).replace(/^gid:\/\/shopify\/\w+\//, '');
  }

  function gbAusSku(sku) {
    const treffer = /^GC-TR-(\d+)$/.exec(String(sku || '').trim());
    return treffer ? String(Number(treffer[1])) : '';
  }

  function stufe(sku) {
    const gb = gbAusSku(sku);
    return gb ? gb + 'gb' : '';
  }

  function ganzzahl(wert) {
    const zahl = Number.parseInt(String(wert ?? '').trim(), 10);
    return Number.isFinite(zahl) ? zahl : null;
  }

  function summe(items) {
    const cent = items.reduce((s, i) => s + Math.round((Number(i.price) || 0) * 100) * (Number(i.quantity) || 0), 0);
    return cent / 100;
  }

  const katalogJeSku = {};
  const katalogJeVariante = {};
  const katalogJeHandle = {};
  for (const eintrag of katalog) {
    if (!eintrag || !eintrag.item) continue;
    if (eintrag.sku) katalogJeSku[String(eintrag.sku)] = eintrag;
    if (eintrag.variant_id) katalogJeVariante[nummer(eintrag.variant_id)] = eintrag;
    if (eintrag.handle) katalogJeHandle[String(eintrag.handle)] = eintrag;
  }

  /** Item ohne Listenfelder, mit eigener Menge. */
  function itemAusEintrag(eintrag, menge, liste) {
    const item = Object.assign({}, eintrag.item);
    delete item.index;
    delete item.item_list_id;
    delete item.item_list_name;
    if (menge) item.quantity = menge;
    if (liste && liste.id) item.item_list_id = liste.id;
    if (liste && liste.name) item.item_list_name = liste.name;
    return item;
  }

  /** Item aus einer Zeile der Cart-API (product_id, variant_id, sku, price in Cent, quantity). */
  function itemAusZeile(zeile, menge) {
    const sku = String(zeile.sku || '').trim();
    const eintrag = katalogJeSku[sku];
    const produktId = nummer(zeile.product_id);
    const variantenId = nummer(zeile.variant_id);
    const cent = typeof zeile.final_price === 'number' ? zeile.final_price : Number(zeile.price || 0);
    const item = {
      item_id: produktId && variantenId ? 'shopify_ZZ_' + produktId + '_' + variantenId : variantenId || produktId,
      item_name: eintrag ? eintrag.item.item_name : String(zeile.product_title || zeile.title || ''),
      item_brand: 'GurbetciCard',
      item_category: 'eSIM',
      item_category2: 'Türkiye',
      price: Math.round(cent) / 100,
      quantity: menge,
    };
    const gb = gbAusSku(sku);
    if (gb) item.item_category3 = gb + ' GB';
    if (sku) item.item_variant = sku;
    if (typeof zeile.original_price === 'number' && zeile.original_price > cent) {
      item.discount = Math.round(zeile.original_price - cent) / 100;
    }
    return item;
  }

  /* --------------------------------------------------------- Warenkorb-Stand */

  let stand = null; // { key: { quantity, zeile } }, null bis der erste Stand gelesen ist
  let korbAttribute = {};
  let waehrung = String(kontext.currency || 'EUR');
  let kette = Promise.resolve();
  const formularMerker = { variante: '', zeit: 0 };

  function cartArt(url) {
    try {
      const pfad = new URL(String(url || ''), w.location.origin).pathname;
      const treffer = /\/cart\/(add|change|update|clear)(?:\.js)?$/.exec(pfad);
      return treffer ? treffer[1] : '';
    } catch (fehler) {
      return '';
    }
  }

  function istKorb(daten) {
    return !!(daten && Array.isArray(daten.items) && typeof daten.item_count === 'number');
  }

  function alsStand(korb) {
    const neu = {};
    for (const zeile of Array.isArray(korb.items) ? korb.items : []) {
      if (zeile && zeile.key) neu[String(zeile.key)] = { quantity: Number(zeile.quantity) || 0, zeile };
    }
    if (korb.currency) waehrung = String(korb.currency);
    korbAttribute = korb.attributes && typeof korb.attributes === 'object' ? korb.attributes : {};
    return neu;
  }

  function korbLesen() {
    if (!urFetch) return Promise.resolve(null);
    return urFetch
      .call(w, '/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((antwort) => (antwort && antwort.ok ? antwort.json() : null))
      .catch(() => null);
  }

  function anzahlImKorb() {
    if (!stand) return 0;
    return Object.keys(stand).reduce((s, k) => s + stand[k].quantity, 0);
  }

  function uebernehmen(korb) {
    const neu = alsStand(korb);
    if (stand) {
      const hinzu = [];
      const weg = [];
      for (const key of Object.keys(neu)) {
        const vorher = stand[key] ? stand[key].quantity : 0;
        const differenz = neu[key].quantity - vorher;
        if (differenz > 0) {
          // Der Formular-Rückfall hat dieselbe Variante gerade schon gemeldet.
          const zeile = neu[key].zeile;
          const doppelt =
            formularMerker.variante &&
            formularMerker.variante === nummer(zeile.variant_id) &&
            Date.now() - formularMerker.zeit < 5000;
          if (!doppelt) hinzu.push(itemAusZeile(zeile, differenz));
        } else if (differenz < 0) {
          weg.push(itemAusZeile(neu[key].zeile, -differenz));
        }
      }
      for (const key of Object.keys(stand)) {
        if (!neu[key] && stand[key].quantity > 0) weg.push(itemAusZeile(stand[key].zeile, stand[key].quantity));
      }
      if (hinzu.length) ecommerce('add_to_cart', { currency: waehrung, value: summe(hinzu), items: hinzu });
      if (weg.length) ecommerce('remove_from_cart', { currency: waehrung, value: summe(weg), items: weg });
      stand = neu;
      if (hinzu.length) cidErfassen('add');
      return;
    }
    stand = neu;
  }

  function nachMutation(art, antwortJson) {
    kette = kette
      .then(async () => {
        let korb = istKorb(antwortJson) ? antwortJson : null;
        if (!korb) korb = await korbLesen();
        if (korb) uebernehmen(korb, art);
      })
      .catch(() => {});
  }

  /* ---------------------------------------------------------------- Hooks (a) */

  if (urFetch) {
    w.fetch = function (eingabe, init) {
      const ergebnis = urFetch.apply(this, arguments);
      try {
        const url = typeof eingabe === 'string' ? eingabe : (eingabe && eingabe.url) || '';
        const methode = String((init && init.method) || (eingabe && eingabe.method) || 'GET').toUpperCase();
        const art = cartArt(url);
        if (art && methode === 'POST' && ergebnis && typeof ergebnis.then === 'function') {
          ergebnis.then(
            (antwort) => {
              if (!antwort || !antwort.ok) return;
              let klon = null;
              try {
                klon = antwort.clone();
              } catch (fehler) {
                klon = null;
              }
              if (!klon) {
                nachMutation(art, null);
                return;
              }
              klon.json().then(
                (json) => nachMutation(art, json),
                () => nachMutation(art, null)
              );
            },
            () => {}
          );
        }
      } catch (fehler) {
        /* der eigentliche Aufruf ist davon unberührt */
      }
      return ergebnis;
    };
  }

  try {
    const urOpen = XMLHttpRequest.prototype.open;
    const urSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (methode, url) {
      try {
        this.__gcCartArt = String(methode || '').toUpperCase() === 'POST' ? cartArt(url) : '';
      } catch (fehler) {
        this.__gcCartArt = '';
      }
      return urOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      try {
        if (this.__gcCartArt) {
          const art = this.__gcCartArt;
          this.addEventListener('load', () => {
            if (this.status >= 200 && this.status < 300) {
              let json = null;
              try {
                json = JSON.parse(this.responseText);
              } catch (fehler) {
                json = null;
              }
              nachMutation(art, json);
            }
          });
        }
      } catch (fehler) {
        /* unberührt */
      }
      return urSend.apply(this, arguments);
    };
  } catch (fehler) {
    /* ohne XHR-Hook läuft der fetch-Hook allein */
  }

  // Formular-Rückfall: ein echtes Absenden auf /cart/add (ohne Skript-Abfang) verlässt die
  // Seite; das Ereignis wird vorher aus den Formularfeldern und dem Katalog gebildet.
  d.addEventListener(
    'submit',
    (ereignis) => {
      try {
        const formular = ereignis.target;
        if (!(formular instanceof HTMLFormElement)) return;
        if (cartArt(formular.getAttribute('action') || '') !== 'add') return;
        setTimeout(() => {
          try {
            if (ereignis.defaultPrevented) return;
            const idFeld = formular.querySelector('input[name="id"]:checked, input[name="id"]:not([type="radio"]), select[name="id"]');
            const variante = nummer(idFeld && idFeld.value);
            const mengeFeld = formular.querySelector('[name="quantity"]');
            const menge = Math.max(ganzzahl(mengeFeld && mengeFeld.value) ?? 1, 1);
            const eintrag = katalogJeVariante[variante];
            if (!eintrag) return;
            formularMerker.variante = variante;
            formularMerker.zeit = Date.now();
            const item = itemAusEintrag(eintrag, menge);
            ecommerce('add_to_cart', { currency: waehrung, value: summe([item]), items: [item] });
          } catch (fehler) {
            /* nie blockieren */
          }
        }, 0);
      } catch (fehler) {
        /* nie blockieren */
      }
    },
    false
  );

  /* ------------------------------------------------------------ select_item (b) */

  function selectItem(item, quelle, liste) {
    const daten = { items: [item] };
    if (liste && liste.id) daten.item_list_id = liste.id;
    if (liste && liste.name) daten.item_list_name = liste.name;
    push({ ecommerce: null });
    push({ event: 'select_item', select_source: quelle, ecommerce: daten });
  }

  const startseitenListe = { id: 'startseite', name: 'Startseite Pakete' };

  d.addEventListener(
    'click',
    (ereignis) => {
      try {
        const ziel = ereignis.target instanceof Element ? ereignis.target : null;
        if (!ziel) return;

        // Ausdrückliches Datenattribut: data-gc-item trägt das fertige Item als JSON.
        const mitDaten = ziel.closest('[data-gc-item]');
        if (mitDaten) {
          const item = JSON.parse(mitDaten.getAttribute('data-gc-item') || 'null');
          if (item && typeof item === 'object') selectItem(item, 'data-gc-item', null);
          return;
        }

        // Tarifkarte der Startseite (.gc-plan) oder Horizon-Produktkarte: Produkt über den Link-Handle.
        const karte = ziel.closest('.gc-plan, product-card, .product-card');
        const link = ziel.closest('a[href*="/products/"]') || (karte ? karte.querySelector('a[href*="/products/"]') : null);
        if (!link) return;
        const treffer = /\/products\/([^/?#]+)/.exec(link.getAttribute('href') || '');
        if (!treffer) return;
        const eintrag = katalogJeHandle[decodeURIComponent(treffer[1])];
        if (!eintrag) return;
        const inTarifkarten = !!ziel.closest('.gc-plans');
        const liste = inTarifkarten
          ? startseitenListe
          : kontext.page_type === 'collection'
            ? null
            : startseitenListe;
        selectItem(itemAusEintrag(eintrag, 1, liste), inTarifkarten ? 'tarifkarte' : 'produktkarte', liste);
      } catch (fehler) {
        /* nie blockieren */
      }
    },
    true
  );

  /* ----------------------------------------- change: Kachel, Sprache, Kaufbox (b, c, d) */

  function orteDerBox(box) {
    try {
      const liste = JSON.parse((box && box.getAttribute('data-gc-orte')) || '[]');
      const karte = {};
      for (const ort of Array.isArray(liste) ? liste : []) {
        if (ort && ort.handle) karte[String(ort.handle)] = { region: String(ort.region || '') };
      }
      return karte;
    } catch (fehler) {
      return {};
    }
  }

  function stufeDerBox(box) {
    if (!box) return String(kontext.product_tier || '');
    const kachel = box.querySelector('[data-gc-tile]:checked') || box.querySelector('[data-gc-tile]');
    return kachel ? stufe(kachel.getAttribute('data-gc-paketcode')) || String(kontext.product_tier || '') : String(kontext.product_tier || '');
  }

  function tageZwischen(start, ende) {
    const a = Date.parse(start + 'T00:00:00Z');
    const b = Date.parse(ende + 'T00:00:00Z');
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
    return Math.round((b - a) / 86400000);
  }

  function heuteIso() {
    const jetzt = new Date();
    return jetzt.getFullYear() + '-' + String(jetzt.getMonth() + 1).padStart(2, '0') + '-' + String(jetzt.getDate()).padStart(2, '0');
  }

  d.addEventListener(
    'change',
    (ereignis) => {
      try {
        const element = ereignis.target instanceof Element ? ereignis.target : null;
        if (!element) return;

        // (b) Paketkachel in der Kaufbox der Startseite
        if (element.matches('input[data-gc-tile][type="radio"]') && element.checked) {
          const sku = String(element.getAttribute('data-gc-paketcode') || '').trim();
          const eintrag = katalogJeSku[sku] || katalogJeVariante[nummer(element.value)];
          if (eintrag) {
            selectItem(itemAusEintrag(eintrag, 1, startseitenListe), 'kaufbox', startseitenListe);
          }
          return;
        }

        // (c) Sprachwechsel im Lokalisierungsformular
        if (element.matches('select[name="language_code"]') && element.closest('form[action*="localization"]')) {
          push({
            event: 'gc_language_switch',
            switch_type: 'language',
            language_from: String(kontext.language || ''),
            language_to: String(element.value || '').toLowerCase(),
            country_from: String(kontext.country || ''),
          });
          return;
        }

        // (d) Reiseziel
        if (element.matches('[data-gc-ziel]')) {
          const box = element.closest('[data-gc-buybox]');
          const handle = String(element.value || '').trim();
          if (!handle) return;
          const ort = orteDerBox(box)[handle] || { region: '' };
          push({
            event: 'gc_destination_select',
            destination: 'tr',
            destination_place: handle,
            destination_region: ort.region,
            product_tier: stufeDerBox(box),
          });
          return;
        }

        // (d) Reisezeitraum, sobald Beginn und Ende stehen
        if (element.matches('[data-gc-start], [data-gc-ende]')) {
          const box = element.closest('[data-gc-buybox]');
          if (!box) return;
          const startFeld = box.querySelector('[data-gc-start]');
          const endeFeld = box.querySelector('[data-gc-ende]');
          const start = String((startFeld && startFeld.value) || '').trim();
          const ende = String((endeFeld && endeFeld.value) || '').trim();
          if (!start || !ende) return;
          const tageFeld = box.querySelector('[data-gc-tage]');
          const tage = ganzzahl(tageFeld && tageFeld.value) ?? tageZwischen(start, ende);
          const vorlauf = tageZwischen(heuteIso(), start);
          if (tage === null || tage < 0) return;
          push({
            event: 'gc_travel_dates',
            destination: 'tr',
            travel_days: tage,
            travel_lead_days: vorlauf === null ? undefined : vorlauf,
            product_tier: stufeDerBox(box),
          });
        }
      } catch (fehler) {
        /* nie blockieren */
      }
    },
    true
  );

  // (c) Länderwechsel: Horizon setzt ein verborgenes Feld und sendet das Formular ab.
  d.addEventListener(
    'click',
    (ereignis) => {
      try {
        const ziel = ereignis.target instanceof Element ? ereignis.target : null;
        const eintrag = ziel && ziel.closest('form[action*="localization"] [role="option"][data-value]');
        if (!eintrag) return;
        const land = String(eintrag.getAttribute('data-value') || '').toUpperCase();
        if (!land || land === String(kontext.country || '')) return;
        push({
          event: 'gc_language_switch',
          switch_type: 'country',
          language_from: String(kontext.language || ''),
          country_from: String(kontext.country || ''),
          country_to: land,
        });
      } catch (fehler) {
        /* nie blockieren */
      }
    },
    true
  );

  /* ------------------------------------------------------------ cid-Erfassung (e) */

  function mitPrivacy(rueckruf) {
    try {
      if (w.Shopify && w.Shopify.customerPrivacy) {
        rueckruf(w.Shopify.customerPrivacy);
        return;
      }
      if (w.Shopify && typeof w.Shopify.loadFeatures === 'function') {
        w.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (fehler) => {
          rueckruf(!fehler && w.Shopify && w.Shopify.customerPrivacy ? w.Shopify.customerPrivacy : null);
        });
        return;
      }
    } catch (fehler) {
      /* fällt unten auf null */
    }
    rueckruf(null);
  }

  function einwilligung(cp) {
    let analytics = false;
    let marketing = false;
    try {
      analytics = typeof cp.analyticsProcessingAllowed === 'function' && cp.analyticsProcessingAllowed() === true;
    } catch (fehler) {
      analytics = false;
    }
    try {
      marketing = typeof cp.marketingAllowed === 'function' && cp.marketingAllowed() === true;
    } catch (fehler) {
      marketing = false;
    }
    return { analytics, marketing, code: 'a' + (analytics ? '1' : '0') + 'm' + (marketing ? '1' : '0') };
  }

  // ---------------------------------------------------------------------------
  // Herkunft: Kampagnenparameter und Klick-Kennungen, jeweils mit eigenem Fenster.
  //
  // Warum gepuffert und nicht bei jedem Treffer neu gelesen: Wer über eine Anzeige
  // kommt, sich umsieht und erst zwei Tage später kauft, hat beim Kauf keine
  // Parameter mehr in der Adresse. Ohne Puffer trüge der Kauf die Herkunft
  // "direkt" — die Anzeige, die ihn gebracht hat, bekäme ihn nie zugeschrieben.
  //
  // Warum zwei verschiedene Fenster: Eine Klick-Kennung ist ein harter Beleg für
  // einen bezahlten Klick und gilt bei Google 90 Tage. Kampagnenparameter sind
  // dagegen leicht zu erben, etwa über einen geteilten Link; ein langes Fenster
  // schriebe dann fremde Besuche der alten Kampagne zu. Deshalb 30 Minuten, das
  // Maß einer Sitzung. Beides ist das Muster aus dem Kundenprojekt Dr. Hatun.
  // ---------------------------------------------------------------------------
  // Seit dem 07.09.2026 gehören die vier gc_-Parameter der Anzeigen-Nachverfolgung dazu:
  // Die Kampagnen sind auf fünf verdichtet (DEC-088), utm_campaign trägt nur noch Art und
  // Segment. Welche Sprache, Datenstufe, Anzeigengruppe und Übereinstimmungsart den Klick
  // gebracht haben, steht allein in gc_sprache, gc_stufe, gc_gruppe und gc_treffer — und
  // bis zu diesem Tag las kein Messweg sie. Sie teilen das 30-Minuten-Fenster der
  // utm-Parameter, weil sie aus derselben Adresse stammen. utm_term steht bewusst zuletzt:
  // Es ist das längste Feld, und die Kompaktform wird bei 250 Zeichen abgeschnitten.
  const UTM_FELDER = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_id', 'utm_content',
    'gc_sprache', 'gc_stufe', 'gc_gruppe', 'gc_treffer', 'utm_term'];
  const KLICK_FELDER = ['gclid', 'gbraid', 'wbraid', 'dclid'];
  const UTM_FENSTER_MS = 30 * 60 * 1000;
  const KLICK_FENSTER_MS = 90 * 24 * 60 * 60 * 1000;

  function abfrage() {
    try {
      return new URLSearchParams(w.location.search);
    } catch (fehler) {
      return new URLSearchParams('');
    }
  }

  // Zwischenspeicher im Fenster, nicht im Gerät. Er lebt genau so lange wie diese
  // Seite und ist damit kein Zugriff, der eine Einwilligung braucht.
  let herkunftOffen = null;

  /** Liest die Herkunft aus der Adresse. Schreibt nichts — dafür ist die Einwilligung nötig. */
  function herkunftAusAdresse() {
    const q = abfrage();
    const jetzt = Date.now();
    const utm = {};
    for (const feld of UTM_FELDER) {
      const wert = q.get(feld);
      if (wert) utm[feld] = wert;
    }
    const klick = {};
    for (const feld of KLICK_FELDER) {
      const wert = q.get(feld);
      if (wert) klick[feld] = wert;
    }
    if (!Object.keys(utm).length && !Object.keys(klick).length) return null;
    return { utm, klick, _t: jetzt };
  }

  /** Schreibt die offene Herkunft ins Gerät. Aufruf ausschließlich nach erteilter Einwilligung. */
  function herkunftSchreiben() {
    if (!herkunftOffen) return;
    const jetzt = herkunftOffen._t;
    if (Object.keys(herkunftOffen.utm).length) {
      const utm = Object.assign({}, herkunftOffen.utm, { _t: jetzt });
      try { speicher('local', '_gc_utm', JSON.stringify(utm)); } catch (fehler) { /* Speicher voll oder gesperrt */ }
    }
    for (const feld of KLICK_FELDER) {
      const wert = herkunftOffen.klick[feld];
      if (wert) {
        try { speicher('local', '_gc_' + feld, JSON.stringify({ w: wert, _t: jetzt })); } catch (fehler) { /* s. o. */ }
      }
    }
  }

  /** Räumt jede Spur der Herkunft aus dem Gerät. Beim Widerruf und ohne Einwilligung. */
  function herkunftRaeumen() {
    try {
      speicher('local', '_gc_utm', null);
      for (const feld of KLICK_FELDER) speicher('local', '_gc_' + feld, null);
    } catch (fehler) {
      /* gesperrter Speicher ist kein Grund, den Rest anzuhalten */
    }
  }

  function ausSpeicher(schluessel, fenster) {
    try {
      const roh = speicher('local', schluessel);
      if (!roh) return null;
      const d = JSON.parse(roh);
      // Abgelaufenes wird gelöscht, nicht nur übergangen. Sonst liegt es länger im
      // Gerät, als es wirkt — und niemand könnte das an der Wirkung erkennen.
      if (!d || typeof d._t !== 'number' || Date.now() - d._t > fenster) {
        speicher('local', schluessel, null);
        return null;
      }
      return d;
    } catch (fehler) {
      return null;
    }
  }

  /** Die gültige Herkunft dieses Besuchs: Adresse zuerst, sonst der Puffer im Fenster. */
  function herkunftLesen() {
    const ergebnis = {};
    const utm = ausSpeicher('_gc_utm', UTM_FENSTER_MS);
    if (utm) {
      for (const feld of UTM_FELDER) {
        if (utm[feld]) ergebnis[feld] = utm[feld];
      }
    }
    for (const feld of KLICK_FELDER) {
      const d = ausSpeicher('_gc_' + feld, KLICK_FENSTER_MS);
      if (d && d.w) ergebnis[feld] = d.w;
    }
    // Die Herkunft dieser Seite gilt auch dann, wenn sie noch nicht im Gerät steht:
    // Wer über eine Anzeige kommt und auf derselben Seite zustimmt, soll seine
    // Kampagne behalten. Die Adresse hat Vorrang, sie ist die frischere Quelle.
    if (herkunftOffen) {
      for (const feld of UTM_FELDER) {
        if (herkunftOffen.utm[feld]) ergebnis[feld] = herkunftOffen.utm[feld];
      }
      for (const feld of KLICK_FELDER) {
        if (herkunftOffen.klick[feld]) ergebnis[feld] = herkunftOffen.klick[feld];
      }
    }
    return ergebnis;
  }

  // Klick-ID: bevorzugt die gepufferte Kennung, sonst der Linker-Cookie _gcl_aw (GCL.<ts>.<id>).
  function gclidLesen() {
    const h = herkunftLesen();
    const gclid = h.gclid || h.gbraid || h.wbraid || '';
    if (gclid) return gclid;
    const treffer = /GCL\.\d+\.(.+)/.exec(cookie('_gcl_aw'));
    return treffer ? treffer[1] : '';
  }

  /** Kompakte Herkunft für das Warenkorb-Attribut: kurz halten, die Bestellung trägt es mit. */
  function herkunftKompakt() {
    const h = herkunftLesen();
    const teile = [];
    for (const feld of UTM_FELDER) {
      if (h[feld]) teile.push(feld.replace(/^(utm_|gc_)/, '') + '=' + h[feld]);
    }
    return teile.join('|').slice(0, 250);
  }

  // Beim ersten Lauf festhalten, womit dieser Besuch begonnen hat. Muss vor der
  // Warenkorb-Erfassung stehen, sonst trägt die Bestellung eine leere Herkunft.
  //
  // Bis zum 05.09.2026 schrieb diese Zeile den Puffer sofort ins Gerät — auch dann,
  // wenn die Besucherin noch nichts entschieden oder gerade abgelehnt hatte. Jetzt
  // wird nur gelesen; ins Gerät kommt die Herkunft erst mit der Einwilligung, in
  // cidErfassen. Der Zwischenspeicher lebt nur so lange wie diese Seite.
  herkunftOffen = herkunftAusAdresse();

  function gleicheAttribute(attribute) {
    for (const schluessel of Object.keys(attribute)) {
      const ist = korbAttribute[schluessel];
      const soll = attribute[schluessel];
      if ((ist == null ? '' : String(ist)) !== soll) return false;
    }
    return true;
  }

  function cidErfassen(anlass) {
    mitPrivacy((cp) => {
      try {
        if (!cp || !urFetch) return;
        const z = einwilligung(cp);
        let attribute;
        if (!z.analytics) {
          // Keine Einwilligung, also liegt nichts von uns im Gerät. Das Räumen steht
          // bewusst VOR dem Ausstieg unten: Wer gleich zu Beginn ablehnt, hat nie ein
          // Warenkorb-Attribut bekommen — der Herkunftspuffer wäre sonst nie geräumt
          // worden, obwohl genau er gemeint ist.
          herkunftRaeumen();
          // Der flüchtige Zwischenspeicher wird hier bewusst NICHT geleert. Gemessen am
          // 05.09.2026: Wer die Seite über eine Anzeige betritt und erst danach im Banner
          // zustimmt, durchläuft diesen Zweig einmal mit "noch nicht entschieden". Ein
          // Leeren an dieser Stelle löschte die Herkunft, bevor die Zustimmung sie
          // freigibt — die Kampagne des Besuchs wäre verloren, und zwar bei jedem
          // Besucher in einem Pflichtland. Der Zwischenspeicher liegt im Fenster, nicht
          // im Gerät; er stirbt mit der Seite und wird ohne Einwilligung nie geschrieben.
          // Die Warenkorb-Attribute nur zurücksetzen, wenn vorher welche gesetzt wurden.
          if (!speicher('session', '_gc_cid_gesetzt')) return;
          attribute = { _ga_cid: '', _gclid: '', _gc_consent: 'a0m0', _gc_herkunft: '' };
        } else {
          // Jetzt liegt die Einwilligung vor: erst hier darf die Herkunft ins Gerät.
          herkunftSchreiben();
          const treffer = /GA\d+\.\d+\.(.+)/.exec(cookie('_ga'));
          const cid = treffer ? treffer[1] : '';
          if (!cid) return; // das Google-Tag setzt _ga erst nach dem Laden; späterer Anlauf
          attribute = {
            _ga_cid: cid,
            _gclid: z.marketing ? gclidLesen() : '',
            _gc_consent: z.code,
            // Die Kampagnenherkunft geht auch ohne Marketing-Einwilligung mit: Sie sagt,
            // über welche Anzeige der Besuch kam, und benennt keine Person. Die
            // Klick-Kennung bleibt der Marketing-Einwilligung vorbehalten.
            _gc_herkunft: herkunftKompakt()
          };
          if (anlass !== 'add' && anzahlImKorb() === 0) return; // ein leerer Korb trägt keine Bestellung
        }
        const schluessel = JSON.stringify(attribute);
        if (speicher('session', '_gc_cid_gesetzt') === schluessel) return;
        if (gleicheAttribute(attribute)) {
          speicher('session', '_gc_cid_gesetzt', schluessel);
          return;
        }
        kette = kette
          .then(() =>
            urFetch
              .call(w, '/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                credentials: 'same-origin',
                body: JSON.stringify({ attributes: attribute }),
              })
              .then((antwort) => (antwort && antwort.ok ? antwort.json() : null))
              .then((korb) => {
                if (!korb) return;
                speicher('session', '_gc_cid_gesetzt', schluessel);
                if (istKorb(korb)) stand = alsStand(korb);
              })
          )
          .catch(() => {});
      } catch (fehler) {
        /* nie blockieren */
      }
    });
  }

  /* ------------------------------------------------------------------- Start */

  kette = kette
    .then(() => korbLesen())
    .then((korb) => {
      if (korb) stand = alsStand(korb);
    })
    .catch(() => {})
    .then(() => cidErfassen('load'));

  // Das _ga-Cookie entsteht erst, wenn das Google-Tag über GTM gelaufen ist.
  setTimeout(() => cidErfassen('spaet'), 3000);
  d.addEventListener('visitorConsentCollected', () => {
    try {
      speicher('session', '_gc_cid_gesetzt', null);
    } catch (fehler) {
      /* egal */
    }
    setTimeout(() => cidErfassen('consent'), 1500);
  });

  // Kleiner öffentlicher Weg für weitere Bausteine (z. B. Gerätecheck): gc-Ereignis mit Parametern.
  gc.ereignis = function (name, parameter) {
    try {
      const daten = Object.assign({}, parameter || {});
      daten.event = String(name || '');
      if (daten.event.indexOf('gc_') !== 0) daten.event = 'gc_' + daten.event;
      push(daten);
    } catch (fehler) {
      /* nie blockieren */
    }
  };
})(window, document);
